/** "Needs you" — the list of things blocking the income statement or
 * waiting on an owner/operator action. Renders nothing when empty. Pure/
 * presentational: FinancePage builds the item list and owns any dialogs the
 * actions open. */
import { Button, Card, cn } from '../../../design';

export interface NeedsYouAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Shows a spinner and disables the button (e.g. a recompute in flight). */
  pending?: boolean;
  variant?: 'primary' | 'secondary';
}

export interface NeedsYouItem {
  id: string;
  /** 'YYYY-MM', when the item is about one specific month. */
  month?: string;
  title: string;
  detail: string;
  /** Superscript marker matching the income statement's footnote on the
   * cell(s) this item explains — omitted when the item doesn't cover a
   * table cell (e.g. a COGS recompute). */
  marker?: string;
  action?: NeedsYouAction;
}

export function NeedsYou({ items }: { items: readonly NeedsYouItem[] }) {
  if (items.length === 0) return null;
  return (
    <section aria-labelledby="needs-you-heading" className="space-y-3">
      <h2 id="needs-you-heading" className="text-lg font-medium text-fg-primary">
        Holding up the numbers
      </h2>
      <div className="space-y-3">
        {items.map((item) => (
          <Card key={item.id} className={cn(item.action?.pending && 'skeleton')}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-fg-primary">
                  {item.marker && (
                    <sup className="mr-1 text-fg-secondary font-medium">{item.marker}</sup>
                  )}
                  {item.title}
                </p>
                {item.detail && <p className="text-sm text-fg-secondary mt-1">{item.detail}</p>}
              </div>
              {item.action && (
                <Button
                  variant={item.action.variant ?? 'primary'}
                  size="sm"
                  disabled={item.action.disabled}
                  loading={item.action.pending}
                  onClick={item.action.onClick}
                >
                  {item.action.label}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}
